import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Grid, Text } from '@chakra-ui/react';
import { IGame, State } from '../../../providers/Games/useGames';
import { TableHeader } from '../../../components/TableElements/tableHeader';
import { t } from 'i18next';
import { containerMaxWXl } from '../../../components/ContentContainer/contentContainer';
import { Game } from '../../Games/Game/game';
import { IGameToken } from '../user';
import { GameLoader } from 'src/components/GameLoader/gameLoader';

enum GamesState {
	LOADING,
	FETCHED,
	EMPTY,
}

export const Games = ({
	data,
	state,
	userGameTokens,
	isUserGameDataLoading,
}: {
	data: IGame[];
	state: State;
	userGameTokens: IGameToken[];
	isUserGameDataLoading: boolean;
}) => {
	const [dataCombined, setDataCombined] = useState<(IGame & IGameToken)[]>([]);

	const gameList = useMemo(
		() =>
			dataCombined.map((game, index) => (
				<Game
					key={`${game.address}_${index}`}
					data={game}
				/>
			)),
		[dataCombined]
	);

	/** Merge game with user data */
	useEffect(() => {
		const combined: Array<IGame & { balance: string }> = [];

		if (isUserGameDataLoading) {
			setDataCombined(combined);

			return;
		}

		for (const entry of data) {
			combined.push({
				...entry,
				balance: userGameTokens.find(element => element.address === entry.address)?.balance || '0',
			});
		}

		setDataCombined(combined);
	}, [data, userGameTokens, isUserGameDataLoading]);

	const typeOptions: { [p: number]: () => React.ReactNode } = {
		[GamesState.LOADING]: () => (
			<Flex
				position="absolute"
				height="250px"
				width="100%"
				alignItems="center"
				gridGap="32px"
			>
				{Array.from(Array(5).keys()).map(id => (
					<GameLoader key={id} />
				))}
			</Flex>
		),
		[GamesState.FETCHED]: () => gameList,
		[GamesState.EMPTY]: () => <Text>This user owns no games yet.</Text>,
	};

	const connectType = useMemo(() => {
		if (state === State.LOADING) {
			return GamesState.LOADING;
		}
		if (state === State.FETCHED && !dataCombined.length) {
			return GamesState.EMPTY;
		}
		if (state === State.FETCHED) {
			return GamesState.FETCHED;
		}
		return GamesState.LOADING;
	}, [state]);

	return (
		<Flex
			w="100%"
			maxW={containerMaxWXl}
			direction="column"
			p={{ base: '0 20px', hashup: 0 }}
			gridGap="32px"
		>
			<TableHeader
				isLoadMore={false}
				onClickRightHeader={() => null as unknown as void}
				loading={false}
				header={t('search.games').toString()}
				headerRightText=""
				headerStyle={{ width: 'auto' }}
			/>
			<Grid
				justifyContent="start"
				minWidth={{ base: 'unset', hashup: '1124px', xl: 'unset' }}
				margin={{ base: 'unset', hashup: 'auto', xl: 'unset' }}
				overflowX="scroll"
				position="relative"
				css={{
					'&::-webkit-scrollbar': {
						width: '0 !important',
					},
					overflow: '-moz-scrollbars-none',
					msOverflowStyle: 'none',
				}}
			>
				<Flex
					flexWrap={{ base: 'nowrap', hashup: 'wrap' }}
					width={{ base: 'unset', hashup: '100%' }}
					columnGap={{ base: '32px', hashup: '4px', xl: '32px' }}
					rowGap="48px"
					minHeight="309px"
					margin="0 auto"
					overflowX="scroll"
					css={{
						'&::-webkit-scrollbar': {
							width: '0 !important',
						},
						overflow: '-moz-scrollbars-none',
						msOverflowStyle: 'none',
					}}
				>
					{typeOptions[connectType]()}
				</Flex>
			</Grid>
		</Flex>
	);
};
